// Approved #05/#22 local review. No provider write or work completion is implied.
//
// R9 companion-state wiring: the subscription-grid (next shipment,
// delivery plan, activity timeline, upcoming orders aside, summary +
// payment + plan controls) is wrapped in the shared DataBoundary.
// Internal signals (review.pending for mid-call, review.error for
// error) convert honestly from the subscription adapter's own state
// machine; the richer sources (loading, empty plans, degraded
// recharge sync, feature-gate off) arrive via companionSignals. The
// Default state visual is unchanged.
//
// W32 (2026-10-20) real-contact wiring: on a real HubSpot session
// (useReviewIdentity().isPreview === false) the screen renders the real
// identity and never the GROUPS / SARAH / MICHAEL fixture (preview only).
//
// W35 (2026-10-20): the real session reads GET /v1/contacts/:id/
// subscription-summary (W34 contact-property sink, see
// screens/_shared/useSubscriptionSummary.ts for the MEASURED bodies) and
// renders read-only cards: product label + SKU, cadence, next charge
// date, charge status, payment type / last4 / expiry / validity, lifetime
// subscription revenue, loyalty points / tier when HubSpot has them.
// `status: 'none'` renders "No active subscription on file for {name}".
// Loading and error (with the api-server errorCode) go through the shared
// DataBoundary. Every Recharge change control stays visible and inert,
// labelled "Recharge changes are paused by the live-write gate"; no
// Recharge call and no write happens from this tab.
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { UpNext } from '../_shared/UpNext.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { Icon } from '../_shared/IconSprite.js';
import { DataBoundary } from '../_shared/DataBoundary.js';
import { useReviewIdentity } from '../_shared/useReviewIdentity.js';
import {
  resolveCompanionState,
  type ScreenCompanionSignals,
} from '../_shared/useScreenCompanionState.js';
import { useActiveCallSignal, mergeMidCallSignal } from '../_shared/useActiveCallSignal.js';
import { SubscriptionDialog } from './components/SubscriptionDialog.js';
import { GROUPS, type GroupKey, type SubscriptionGroup } from './mockData.js';
import { formatUsd } from '../../data/mockPricing.js';
import { SARAH } from '../../data/mockData.js';
import { getReviewReturnTarget, useReviewState, type ReviewReturnTarget } from '../_shared/ReviewState.js';
import {
  ACTION_NAMES, ADDRESSES, PRODUCTS, RECHARGE_GATE_NOTE, STATUS_NAMES, agreeToDraft, allowedAction, applyReceipt,
  calendarTileFor, copy, createChangeRequest, createSubscriptionReviewState, describeCadence, describeChargeStatus,
  describeLoyalty, describePaymentExpiry, describePaymentType, describePaymentValidity, describePlanValue,
  describeSubscriptionStatus, describeSummaryDate, discardDraft, displayDate, editDraft, ensureDraft, finishReason,
  formatUsdAmount, getDraft, merchandiseTotal, paymentBadge, previewSubscriptionAdapter, productSummary,
  unknownChange, type SubscriptionChangeAdapter, type SubscriptionChangeRequest, type SubscriptionDialogAction,
  type SubscriptionInput, type SubscriptionPlan, type SubscriptionReceipt, type SubscriptionReviewState,
} from './subscriptionState.js';
import { hasSubscriptionOnFile, useSubscriptionSummary } from '../_shared/useSubscriptionSummary.js';
import { useScreenRuntime } from '../CurrentActionReview/runtime-clients.js';
import './styles.css';
import './app.css';

function projectGroup(plan: SubscriptionPlan): SubscriptionGroup {
  return { ...GROUPS[plan.id], label: plan.name, productsSummary: productSummary(plan), summaryLabel: productSummary(plan),
    nextMonth: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(new Date(plan.nextDate + 'T12:00:00Z')).toUpperCase(),
    nextDay: plan.nextDate.slice(-2), nextOrderDate: displayDate(plan.nextDate), frequency: 'Every ' + plan.cadence + ' days',
    addressLabel: plan.address === 'barn' ? 'Barn address' : 'Home address', address: ADDRESSES[plan.address] + ' · Example address',
    lines: plan.products.map(p => ({ name: PRODUCTS[p.product].name, qty: p.quantity, size: '1 gallon', square: PRODUCTS[p.product].code, ...(p.product === 'osteo' ? { squareVariant: 'bone' as const } : {}) })),
  };
}

export function SubscriptionsReviewScreen({
  adapter,
  companionSignals,
}: {
  adapter?: SubscriptionChangeAdapter;
  /** R9 test seam for the six companion states. */
  companionSignals?: ScreenCompanionSignals;
} = {}): ReactNode {
  const params = useParams();
  const customerId = params.customerId || SARAH.id;
  const location = useLocation(), navigate = useNavigate();
  const identity = useReviewIdentity(customerId);
  const isPreview = identity.isPreview;
  const firstName = identity.firstName;
  // W35: the W34 contact-property subscription summary. 'skipped' in
  // preview / dev-mock (same gate as the identity hook).
  const summary = useSubscriptionSummary(customerId);
  // W2A upper lane: adapter default comes from the signed WorkClient when
  // a real session is live. Preview-fixture mode (dev-mock or ?preview=)
  // falls back to the local-only preview adapter W1B shipped.
  //
  // The server envelope for SUBSCRIPTION_CHANGE actions is NOT the same
  // shape as `SubscriptionReceipt` (which is a client-only type bound to
  // the preview state machine). The real adapter maps the server's
  // `MutationState` tag into a receipt binding the review state machine
  // can apply. The server has final say on outcome; this layer only
  // translates between protocol shapes.
  const runtime = useScreenRuntime();
  const liveAdapter: SubscriptionChangeAdapter | null = useMemo(() => {
    if (!runtime) return null;
    const client = runtime.work;
    function receiptFor(
      request: SubscriptionChangeRequest,
      outcome: 'pending' | 'unknown' | 'failed',
      message?: string,
    ): SubscriptionReceipt {
      return {
        operationId: request.operationId,
        customerId: request.customerId,
        planId: request.planId,
        action: request.action,
        expectedRevision: request.expectedRevision,
        termsKey: request.termsKey,
        outcome,
        ...(message !== undefined ? { message } : {}),
      };
    }
    const submit = async (request: SubscriptionChangeRequest): Promise<SubscriptionReceipt> => {
      const state = await client.fireMutation({
        actionName: 'SUBSCRIPTION_CHANGE',
        body: { payload: request as unknown as Record<string, unknown> },
        idempotencyKey: request.operationId,
      });
      if (state.kind === 'ambiguous') return receiptFor(request, 'unknown', 'Server returned AMBIGUOUS; retry with the same idempotency key.');
      if (state.kind === 'rate_limited') return receiptFor(request, 'pending', 'Rate limited by the server.');
      if (state.kind === 'network_error') return receiptFor(request, 'unknown', state.message);
      if (state.kind === 'validation_error') return receiptFor(request, 'failed', state.message);
      if (state.kind === 'forbidden') return receiptFor(request, 'failed', state.message);
      if (state.kind === 'auth_failed') return receiptFor(request, 'failed', state.message);
      if (state.kind === 'vendor_error') return receiptFor(request, 'failed', state.message);
      // success: trust the response envelope's receipt payload when the
      // server returns one shaped like SubscriptionReceipt; otherwise
      // return a pending receipt so the UI shows "save result unknown"
      // and the rep hits "Check save result".
      const body = state.response as unknown as { receipt?: SubscriptionReceipt } | SubscriptionReceipt;
      if (body && typeof body === 'object' && 'receipt' in body && body.receipt) return body.receipt;
      if (body && typeof body === 'object' && 'outcome' in body) return body as SubscriptionReceipt;
      return receiptFor(request, 'pending', 'Server did not include a receipt.');
    };
    const check = async (request: SubscriptionChangeRequest): Promise<SubscriptionReceipt> => submit(request);
    return { submit, check };
  }, [runtime]);
  const effectiveAdapter = adapter ?? liveAdapter ?? previewSubscriptionAdapter;
  const [review, setReview] = useReviewState<SubscriptionReviewState>(customerId, 'subscription-editors', () => createSubscriptionReviewState(customerId));
  const [editor, setEditor] = useState<{ customerId: string; action: SubscriptionDialogAction } | null>(null);
  const dialogKey = editor?.customerId === customerId ? editor.action : null;
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true), currentSetter = useRef(setReview), stateRef = useRef(review);
  const inFlight = useRef(new Set<typeof setReview>());
  currentSetter.current = setReview; stateRef.current = review;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setBusy(false); setEditor(null); }, [setReview]);
  const active = review.selected, plan = review.plans[active];
  const groups: Record<GroupKey, SubscriptionGroup> = { barn: projectGroup(review.plans.barn), hydration: projectGroup(review.plans.hydration) };
  const group = groups[active], total = merchandiseTotal(plan);
  const upcoming = (Object.keys(groups) as GroupKey[]).filter(key => review.plans[key].status !== 'cancelled')
    .sort((a, b) => review.plans[a].nextDate.localeCompare(review.plans[b].nextDate));
  const closeEditor = (): void => setEditor(null);
  const setActive = (key: GroupKey): void => {
    if (review.pending) return;
    closeEditor();
    setReview(current => current.pending ? current : { ...current, selected: key, message: '', error: false });
  };
  const openDialog = (key: SubscriptionDialogAction | 'coverage'): void => {
    if (key === 'coverage') {
      if (!review.pending) navigate('/colic-coverage/' + encodeURIComponent(customerId), {
        state: getReviewReturnTarget(location.state, customerId).state,
      });
      return;
    }
    if (review.pending && (review.pending.request.action !== key || review.pending.request.planId !== active)) return;
    if (key !== 'payment') {
      if (!review.pending && !allowedAction(plan, key)) return;
      setReview(current => ensureDraft(current, active, key));
    } else setReview(current => ({ ...current, message: '', error: false }));
    setEditor({ customerId, action: key });
  };
  const updateInput = (input: SubscriptionInput): void => {
    if (dialogKey && dialogKey !== 'payment') setReview(current => editDraft(current, active, dialogKey, input));
  };
  const updateAgreement = (agreed: boolean): void => {
    if (dialogKey && dialogKey !== 'payment') setReview(current => agreeToDraft(current, active, dialogKey, agreed));
  };
  const discard = (): void => {
    if (dialogKey && dialogKey !== 'payment' && !review.pending) {
      setReview(current => discardDraft(current, active, dialogKey)); closeEditor();
    }
  };
  const accept = (request: SubscriptionChangeRequest, receipt: SubscriptionReceipt, returnAfter: boolean, returnTarget: ReviewReturnTarget): void => {
    let confirmed = false;
    setReview(current => {
      const next = applyReceipt(current, request, receipt);
      confirmed = !next.pending && next.plans[request.planId]?.history[0]?.id === request.operationId;
      return next;
    });
    if (confirmed && returnAfter && mounted.current && currentSetter.current === setReview) {
      closeEditor(); navigate(returnTarget.path, { state: returnTarget.state });
    }
  };
  const save = async (returnAfter: boolean): Promise<void> => {
    if (!dialogKey || dialogKey === 'payment' || inFlight.current.has(setReview)) return;
    const current = stateRef.current;
    let request: SubscriptionChangeRequest;
    try { request = createChangeRequest(current, getDraft(current, active, dialogKey), crypto.randomUUID()); }
    catch (error) { setReview(value => ({ ...value, message: error instanceof Error ? error.message : 'Review this change before saving.', error: true })); return; }
    const returnTarget = getReviewReturnTarget(location.state, customerId);
    inFlight.current.add(setReview); setBusy(true);
    setReview(value => ({ ...value, pending: { request: copy(request), phase: 'pending', returnAfter, returnTarget: copy(returnTarget) }, message: 'Saving this preview change…', error: false }));
    try { accept(request, await effectiveAdapter.submit(copy(request)), returnAfter, returnTarget); }
    catch { setReview(value => unknownChange(value, request)); }
    finally { inFlight.current.delete(setReview); if (mounted.current && currentSetter.current === setReview) setBusy(false); }
  };
  const check = async (): Promise<void> => {
    const pending = stateRef.current.pending;
    if (!pending || inFlight.current.has(setReview)) return;
    const { request, returnAfter, returnTarget } = copy(pending);
    inFlight.current.add(setReview); setBusy(true);
    try { accept(request, await effectiveAdapter.check(copy(request)), returnAfter, returnTarget); }
    catch { setReview(value => unknownChange(value, request)); }
    finally { inFlight.current.delete(setReview); if (mounted.current && currentSetter.current === setReview) setBusy(false); }
  };

  // R10: fold the live Kixie mid-call signal into the subscription
  // boundary. The test seam (companionSignals.midCall) still wins so
  // existing unit tests remain hermetic.
  const liveMidCall = useActiveCallSignal({
    customerId,
    label: `Call in progress with ${isPreview ? SARAH.firstName : firstName}`,
    actionName: 'KIXIE_CALL_IN_PROGRESS',
  });
  const mergedSignals = useMemo(
    () => mergeMidCallSignal(companionSignals, liveMidCall),
    [companionSignals, liveMidCall],
  );

  if (!isPreview) {
    // W32: real session, no fixture. W35: read-only cards from the W34
    // subscription summary; every Recharge control stays visible and inert.
    const inert = (label: string, className = 'outlined'): ReactNode => (
      <button className={className} type="button" disabled aria-describedby="subscription-gate-note">{label}</button>
    );
    const data = summary.status === 'ready' ? summary.data : null;
    const onFile = hasSubscriptionOnFile(data);
    const realSignals: ScreenCompanionSignals = {
      ...mergedSignals,
      isLoading: summary.status === 'loading',
      loadingLabel: 'Loading subscription…',
      error:
        mergedSignals.error ??
        (summary.status === 'error'
          ? {
              title: 'Subscription summary could not load',
              detail:
                (summary.errorDetail ?? 'The HubSpot contact-property read for this subscription failed.') +
                (summary.httpStatus ? ` (HTTP ${summary.httpStatus})` : ''),
              errorCode: summary.errorCode ?? 'SUBSCRIPTION_SUMMARY_UNAVAILABLE',
            }
          : null),
    };
    const statusLabel = data ? describeSubscriptionStatus(data.status, data.subscriptionStatusLabel) : '';
    const inactive = !!data && data.status !== 'active';
    const cadence = data ? describeCadence(data.frequencyDays, data.frequencyLabel) : '';
    const nextCharge = data ? describeSummaryDate(data.nextChargeDate) : '';
    const tile = data ? calendarTileFor(data.nextChargeDate) : null;
    const lifetime = data ? formatUsdAmount(data.lifetimeSubRevenue) : null;
    const loyalty = data ? describeLoyalty(data.loyalty.points, data.loyalty.tier) : null;
    const whyNowBody = onFile
      ? `Next charge ${nextCharge} · ${cadence} · ${statusLabel}.`
      : summary.status === 'ready'
        ? `No active subscription on file for ${firstName}.`
        : summary.status === 'error'
          ? 'The subscription summary could not be read from HubSpot.'
          : 'Reading the subscription summary from HubSpot.';
    const loyaltyCard = loyalty ? (
      <section className="card" data-testid="subscription-real-loyalty">
        <div className="section-heading">
          <h3>Loyalty</h3>
          <span>LoyaltyLion on the HubSpot contact</span>
        </div>
        <div className="summary-body">
          <p>{loyalty}</p>
        </div>
      </section>
    ) : null;
    const coverageReminder = (
      <section className="coverage-reminder">
        <Icon id="check" />
        <div>
          <strong>Colic Coverage</strong>
          <p>Check enrollment and any impact of a schedule change.</p>
          <button className="text-btn" type="button" onClick={() => openDialog('coverage')}>
            Review coverage
          </button>
        </div>
      </section>
    );
    return (
      <WorkspaceChrome screen="subscriptions" rail="my-work">
        <div className="breadcrumbs">
          <div>
            My work
            <span className="slash">/</span>
            <strong>{identity.customer.fullName}</strong>
          </div>
          {identity.statusPill ? <span className="preview">{identity.statusPill}</span> : null}
        </div>
        <PriorityFocus
          ariaLabel="SUBSCRIPTION AT A GLANCE"
          eyebrow="SUBSCRIPTION AT A GLANCE"
          heading={`Manage ${firstName}'s subscription`}
          what={{ label: 'WHAT', heading: `Review ${firstName}'s next delivery`, iconId: 'message' }}
          whyNow={{ label: 'WHY NOW', body: whyNowBody, iconId: 'clock' }}
          nextAction={{
            label: 'NEXT ACTION',
            body: onFile
              ? 'Confirm the next charge date and product with the customer on the call.'
              : 'Confirm their current plan on the call and log it in HubSpot.',
            detail: `${RECHARGE_GATE_NOTE}.`,
            iconId: 'target',
          }}
          ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: identity.ownerDetail }}
          owner={identity.owner}
        />
        <IdentityRow customer={identity.customer} badgeLabel={identity.badgeLabel} sinceLabel={identity.sinceLabel} />
        <CustomerTabs customerId={customerId} active="subscription" />
        <SubscriptionBoundary signals={realSignals}>
          {onFile && data ? (
            <div className="subscription-grid" data-testid="subscription-real-summary">
              <div className="subscription-left">
                <section className="card next-shipment" data-testid="subscription-real-next">
                  <div className="card-head">
                    <div className="row">
                      <span className="head-icon">
                        <Icon id="box" />
                      </span>
                      <h3 id="next-sub-title">Next charge</h3>
                    </div>
                    <span className={'active-pill' + (inactive ? ' se-inactive' : '')} data-testid="subscription-real-status">{statusLabel}</span>
                  </div>
                  <div className="shipment-body">
                    <div className="shipment-date">
                      <div className="calendar-tile">
                        <span>{tile?.month ?? '--'}</span>
                        <strong id="calendar-day">{tile?.day ?? '--'}</strong>
                      </div>
                      <div>
                        <span className="eyebrow">Next charge processes</span>
                        <h2 id="next-order-date" data-testid="subscription-real-next-date">{nextCharge}</h2>
                        <p>Charge status: {describeChargeStatus(data.chargeStatus)} · Charge date, not a delivery date</p>
                      </div>
                      {inert('Change date')}
                    </div>
                    <div className="shipment-products" id="selected-products">
                      <div className="shipment-line" data-testid="subscription-real-product">
                        <span className="product-square">SUB</span>
                        <div>
                          <strong>{data.productLabel ?? 'Product not on file'}</strong>
                          <small>{data.productSku ? `SKU ${data.productSku}` : 'SKU not on file'}</small>
                        </div>
                        <span>
                          {cadence}
                          <small>Recharge frequency</small>
                        </span>
                      </div>
                    </div>
                    <div className="shipment-actions">
                      {inert('Edit products & quantities', 'blue-action')}
                      {inert('Skip next order')}
                    </div>
                  </div>
                </section>

                <section className="card" data-testid="subscription-real-plan">
                  <div className="section-heading">
                    <h3>Delivery plan</h3>
                    <span>Recharge settings on the HubSpot contact</span>
                  </div>
                  <div className="plan-settings">
                    <div className="plan-item">
                      <span className="plan-icon">
                        <Icon id="refresh" />
                      </span>
                      <div>
                        <strong id="selected-frequency" data-testid="subscription-real-cadence">{cadence}</strong>
                        <p>{data.frequencyLabel ? `HubSpot frequency label: ${data.frequencyLabel}.` : 'No frequency label on the contact.'}</p>
                      </div>
                      {inert('Change frequency', 'text-btn')}
                    </div>
                    <div className="plan-item">
                      <span className="plan-icon">
                        <Icon id="pin" />
                      </span>
                      <div>
                        <strong id="address-label">Shipping address</strong>
                        <p id="selected-address">The subscription address is not part of the HubSpot summary. Check Recharge before changing it.</p>
                      </div>
                      {inert('Review address', 'text-btn')}
                    </div>
                  </div>
                  <div className="plan-note">
                    <Icon id="check" />
                    <span>Read from the Recharge sync on the HubSpot contact. Recharge itself was not called.</span>
                  </div>
                </section>
              </div>

              <aside className="subscription-right">
                <section className="card subscription-summary" data-testid="subscription-real-facts">
                  <div className="section-heading">
                    <h3>Subscription on file</h3>
                    <span className={'active-pill' + (inactive ? ' se-inactive' : '')}>{statusLabel}</span>
                  </div>
                  <div className="summary-body">
                    <div className="subscriber-row">
                      <div className="avatar">{identity.customer.initials}</div>
                      <div>
                        <strong>{identity.customer.fullName}</strong>
                        <p id="summary-products">{data.productLabel ?? 'Product not on file'}</p>
                      </div>
                    </div>
                    <dl>
                      <dt>Product SKU</dt>
                      <dd data-testid="subscription-real-sku">{data.productSku ?? 'Not on file'}</dd>
                      <dt>Frequency</dt>
                      <dd id="summary-frequency">{cadence}</dd>
                      <dt>Next charge</dt>
                      <dd id="summary-date">{nextCharge}</dd>
                      <dt>Charge status</dt>
                      <dd data-testid="subscription-real-charge-status">{describeChargeStatus(data.chargeStatus)}</dd>
                      <dt>Lifetime subscription revenue</dt>
                      <dd data-testid="subscription-real-lifetime">{lifetime ?? 'Not on file'}</dd>
                      <dt>Recharge customer</dt>
                      <dd data-testid="subscription-real-recharge-id">{data.rechargeCustomerId ?? 'Not on file'}</dd>
                    </dl>
                    <p className="sample-note">
                      Read from HubSpot contact properties written by the Recharge sync. Nothing on this tab is a sample.
                    </p>
                  </div>
                </section>

                <section className="card" data-testid="subscription-real-payment">
                  <div className="section-heading">
                    <h3>Payment method</h3>
                  </div>
                  <div className="payment-method">
                    <span className="visa-mark" id="selected-card-brand">{paymentBadge(data.payment.type)}</span>
                    <div>
                      <strong id="selected-card">
                        {describePaymentType(data.payment.type)}
                        {data.payment.last4 ? ` ending in ${data.payment.last4}` : ''}
                      </strong>
                      <p id="selected-card-expiry">
                        {describePaymentExpiry(data.payment.expMonth, data.payment.expYear)} · {describePaymentValidity(data.payment.valid)}
                      </p>
                    </div>
                  </div>
                  {inert('Update payment method', 'card-link')}
                </section>

                {loyaltyCard}

                <section className="card" data-testid="subscription-real-controls">
                  <div className="section-heading">
                    <h3>Need to adjust the plan?</h3>
                    <span id="subscription-gate-note">{RECHARGE_GATE_NOTE}</span>
                  </div>
                  <div className="plan-controls">
                    {inert('Pause subscription')}
                    {inert('Cancel subscription', 'cancel-link')}
                    <p>{RECHARGE_GATE_NOTE}. Log the customer's request in HubSpot for now.</p>
                  </div>
                </section>

                {coverageReminder}
              </aside>
            </div>
          ) : (
            <>
              <section className="card subscription-summary" data-testid="subscription-real-empty">
                <div className="section-heading">
                  <h3>Subscriptions on file</h3>
                  <span>HubSpot contact</span>
                </div>
                <div className="summary-body">
                  <p><strong>No active subscription on file for {firstName}.</strong></p>
                  <p className="sample-note">
                    HubSpot holds no Recharge subscription status for this contact, so no plan, dates, totals or payment
                    details are shown. Nothing on this tab is a sample.
                  </p>
                </div>
              </section>
              {loyaltyCard}
              <section className="card" data-testid="subscription-real-controls">
                <div className="section-heading">
                  <h3>Need to adjust the plan?</h3>
                  <span id="subscription-gate-note">{RECHARGE_GATE_NOTE}</span>
                </div>
                <div className="plan-controls">
                  {inert('Change date')}
                  {inert('Edit products & quantities', 'blue-action')}
                  {inert('Skip next order')}
                  {inert('Change frequency')}
                  {inert('Review address')}
                  {inert('Update payment method')}
                  {inert('Pause subscription')}
                  {inert('Cancel subscription', 'cancel-link')}
                  <p>{RECHARGE_GATE_NOTE}. Log the customer's request in HubSpot for now.</p>
                </div>
              </section>
              {coverageReminder}
            </>
          )}
        </SubscriptionBoundary>
        <UpNext customerId={customerId} />
        <ActionFooter
          disabled={true}
          noteWhenDisabled={onFile ? 'Subscription review · Read-only' : 'No subscription loaded for this contact'}
          noteWhenEnabled="Ready to continue"
          summaryBody="Log any subscription request in HubSpot before finishing."
        />
      </WorkspaceChrome>
    );
  }

  return (
    <WorkspaceChrome screen="subscriptions" rail="my-work">
      <div className="breadcrumbs">
        <div>
          My work
          <span className="slash">/</span>
          <strong>{SARAH.fullName}</strong>
        </div>
        <span className="preview">Design preview · Example dates, prices and plan</span>
      </div>
      <PriorityFocus
        ariaLabel="SUBSCRIPTION AT A GLANCE"
        eyebrow="SUBSCRIPTION AT A GLANCE"
        heading="Manage Sarah's subscription"
        what={{ label: 'WHAT', heading: "Review Sarah's next delivery", iconId: 'message' }}
        whyNow={{
          label: 'WHY NOW',
          body: "Keep products, timing and payment details aligned with Sarah's needs.",
          iconId: 'clock',
        }}
        nextAction={{
          label: 'NEXT ACTION',
          body: 'Review the next order before making changes.',
          detail: 'Confirm any requested change with Sarah.',
          iconId: 'target',
        }}
        ownership={{
          label: 'OWNERSHIP',
          iconId: 'people',
          detail: 'Primary account owner. Coverage preserves ownership.',
        }}
      />
      <IdentityRow customer={SARAH} badgeLabel="Active subscriber" />
      <CustomerTabs customerId={customerId} active="subscription" />
      {review.pending && !dialogKey && <section className="se-pending-banner" role="status">
        <div><strong>{review.pending.phase === 'unknown' ? 'Save result unknown' : 'Save not confirmed'} · {review.plans[review.pending.request.planId].name}</strong>
          <p>The original change is retained. Check it before another change or finishing.</p></div>
        <button className="outlined" type="button" disabled={busy} onClick={() => void check()}>Check save result</button>
        <button className="outlined" type="button" onClick={() => openDialog(review.pending!.request.action)}>Review original change</button>
      </section>}
      {review.message && !dialogKey && <p className={'se-feedback' + (review.error ? ' se-error' : '')} role={review.error ? 'alert' : 'status'}>{review.message}</p>}
      <SubscriptionBoundary signals={mergedSignals}>
      <section className="subscription-picker card">
        <div className="section-heading">
          <h3>Sarah's subscriptions</h3>
          <span>{Object.values(review.plans).filter(item => item.status === 'active').length} active · Separate schedules</span>
        </div>
        <div className="group-options">
          {(Object.keys(groups) as GroupKey[]).map((key) => {
            const g = groups[key];
            return (
              <button
                key={key}
                type="button"
                className={`group-option${active === key ? ' selected-group' : ''}`}
                aria-pressed={active === key}
                disabled={Boolean(review.pending)}
                onClick={() => setActive(key)}
              >
                <span className={`group-icon${key === 'hydration' ? ' hydration-icon' : ''}`}>
                  <Icon id={key === 'hydration' ? 'refresh' : 'box'} />
                </span>
                <div>
                  <strong>{g.label}</strong>
                  <p>{g.productsSummary}</p>
                  <small>
                    {g.nextMonth} {g.nextDay} · {g.frequency}
                  </small>
                </div>
                <span className="group-selection" />
              </button>
            );
          })}
        </div>
        <div className="group-scope">
          <Icon id="check" />
          <span>
            Viewing <strong id="selected-group-name">{group.label}</strong>. Changes apply only to
            this selected subscription.
          </span>
        </div>
      </section>

      <div className="subscription-grid">
        <div className="subscription-left">
          <section className="card next-shipment">
            <div className="card-head">
              <div className="row">
                <span className="head-icon">
                  <Icon id="box" />
                </span>
                <h3 id="next-sub-title">{group.label} · Next order</h3>
              </div>
              <span className={'active-pill' + (plan.status !== 'active' ? ' se-inactive' : '')}>{STATUS_NAMES[plan.status]}</span>
            </div>
            <div className="shipment-body">
              <div className="shipment-date">
                <div className="calendar-tile">
                  <span>{group.nextMonth}</span>
                  <strong id="calendar-day">{group.nextDay}</strong>
                </div>
                <div>
                  <span className="eyebrow">Next order processes</span>
                  <h2 id="next-order-date">{plan.status === 'cancelled' ? 'No recurring order scheduled' : group.nextOrderDate}</h2>
                  <p>Order date, not a guaranteed delivery date</p>
                </div>
                <button
                  className="outlined"
                  type="button"
                  disabled={Boolean(review.pending) || !allowedAction(plan, 'date')}
                  onClick={() => openDialog('date')}
                >
                  Change date
                </button>
              </div>
              <div className="shipment-products" id="selected-products">
                {group.lines.map((line) => {
                  const unit = Object.values(PRODUCTS).find(product => product.name === line.name)?.price || 0;
                  return (
                    <div className="shipment-line" key={line.name}>
                      <span
                        className={`product-square${line.squareVariant === 'bone' ? ' bone' : ''}`}
                        style={
                          line.square === 'HM'
                            ? { background: '#dbf4f2', color: '#268477' }
                            : undefined
                        }
                      >
                        {line.square}
                      </span>
                      <div>
                        <strong>{line.name}</strong>
                        <small>
                          {line.size} · Quantity {line.qty}
                        </small>
                      </div>
                      <span>
                        {formatUsd(unit * line.qty)}
                        <small>Example line total</small>
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="shipment-total">
                <span>
                  Recurring merchandise total
                  <small>Shipping and tax calculated with the order</small>
                </span>
                <strong id="selected-total">{formatUsd(total)}</strong>
              </div>
              <div className="shipment-actions">
                <button
                  className="blue-action"
                  type="button"
                  disabled={Boolean(review.pending) || !allowedAction(plan, 'products')}
                  onClick={() => openDialog('products')}
                >
                  Edit products &amp; quantities
                </button>
                <button
                  className="outlined"
                  type="button"
                  disabled={Boolean(review.pending) || !allowedAction(plan, 'skip')}
                  onClick={() => openDialog('skip')}
                >
                  Skip next order
                </button>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="section-heading">
              <h3>Delivery plan</h3>
              <span>Subscription settings</span>
            </div>
            <div className="plan-settings">
              <div className="plan-item">
                <span className="plan-icon">
                  <Icon id="refresh" />
                </span>
                <div>
                  <strong id="selected-frequency">{group.frequency}</strong>
                  <p>Example interval. Available options match Recharge.</p>
                </div>
                <button
                  className="text-btn"
                  type="button"
                  disabled={Boolean(review.pending) || !allowedAction(plan, 'frequency')}
                  onClick={() => openDialog('frequency')}
                >
                  Change
                </button>
              </div>
              <div className="plan-item">
                <span className="plan-icon">
                  <Icon id="pin" />
                </span>
                <div>
                  <strong id="address-label">{group.addressLabel}</strong>
                  <p id="selected-address">{group.address}</p>
                </div>
                <button
                  className="text-btn"
                  type="button"
                  disabled={Boolean(review.pending) || !allowedAction(plan, 'address')}
                  onClick={() => openDialog('address')}
                >
                  Review address
                </button>
              </div>
            </div>
            <div className="plan-note">
              <Icon id="check" />
              <span>
                Subscription changes apply only to the plan you select. Review any Colic Coverage
                impact before confirming.
              </span>
            </div>
          </section>

          <section className="card">
            <div className="section-heading">
              <h3 id="activity-title">{group.label} activity</h3>
              <span>Preview history · Selected plan only</span>
            </div>
            <div className="subscription-timeline" id="selected-activity">
              {plan.history.map(event => <article key={event.id}>
                <i /><div><strong>{ACTION_NAMES[event.action]} saved</strong>
                  <p>{describePlanValue(event.before, event.action)} → {describePlanValue(event.after, event.action)}</p>
                  {event.reason && <p>Customer reason: {event.reason.replaceAll('_', ' ')}</p>}
                  {event.note && <p>{event.note}</p>}
                  <small>{new Date(event.at).toLocaleString('en-US', { timeZone: 'America/Chicago' })} · Jamie Dorsey · Saved in preview</small>
                </div>
              </article>)}
              {!plan.history.length && <article><i /><div>
                <strong>{plan.name} schedule recorded</strong><p>{group.frequency} · Next order {displayDate(plan.nextDate, true)}</p>
                <small>Illustrative subscription event · No changes recorded yet</small>
              </div></article>}
            </div>
          </section>
        </div>

        <aside className="subscription-right">
          <section className="card upcoming-card">
            <div className="section-heading">
              <h3>All upcoming orders</h3>
              <span>By date</span>
            </div>
            {upcoming.map((key) => {
              const g = groups[key];
              const gTotal = merchandiseTotal(review.plans[key]);
              return (
                <button
                  key={key}
                  type="button"
                  className={`upcoming-order${active === key ? ' current-upcoming' : ''}`}
                  disabled={Boolean(review.pending)}
                  onClick={() => setActive(key)}
                >
                  <div className="mini-calendar">
                    <span>{g.nextMonth}</span>
                    <strong>{g.nextDay}</strong>
                  </div>
                  <div>
                    <strong>{g.label}</strong>
                    <p>{g.productsSummary}</p>
                    <small>{formatUsd(gTotal)} · Before shipping &amp; tax</small>
                  </div>
                  <Icon id="arrow" />
                </button>
              );
            })}
            {!upcoming.length && <p className="upcoming-note">No recurring orders scheduled in this preview.</p>}
            <p className="upcoming-note">Example processing dates, not delivery promises.</p>
          </section>

          <section className="card subscription-summary">
            <div className="section-heading">
              <h3>Selected subscription</h3>
              <span className={'active-pill' + (plan.status !== 'active' ? ' se-inactive' : '')}>{STATUS_NAMES[plan.status]}</span>
            </div>
            <div className="summary-body">
              <div className="subscriber-row">
                <div className="avatar">{SARAH.initials}</div>
                <div>
                  <strong>{SARAH.fullName}</strong>
                  <p id="summary-products">{group.summaryLabel}</p>
                </div>
              </div>
              <dl>
                <dt>Frequency</dt>
                <dd id="summary-frequency">{group.frequency}</dd>
                <dt>Next order</dt>
                <dd id="summary-date">
                  {plan.status === 'cancelled' ? 'Cancelled' : displayDate(plan.nextDate, true)}
                </dd>
                <dt>Merchandise</dt>
                <dd id="summary-total">{formatUsd(total)} / order</dd>
                <dt>Price program</dt>
                <dd>Source verification required</dd>
              </dl>
              <p className="sample-note">
                All dates, amounts and payment details shown are illustrative.
              </p>
            </div>
          </section>

          <section className="card">
            <div className="section-heading">
              <h3>Payment method</h3>
            </div>
            <div className="payment-method">
              <span className="visa-mark" id="selected-card-brand">
                {group.cardBrand}
              </span>
              <div>
                <strong id="selected-card">Ending in {group.cardLast4}</strong>
                <p id="selected-card-expiry">{group.cardExpiry}</p>
              </div>
            </div>
            <button
              className="card-link"
              type="button"
              disabled={Boolean(review.pending)}
              onClick={() => openDialog('payment')}
            >
              Update payment method
              <Icon id="arrow" />
            </button>
          </section>

          <section className="card">
            <div className="section-heading">
              <h3>Need to adjust the plan?</h3>
            </div>
            <div className="plan-controls">
              {plan.status === 'paused' && <button className="outlined" type="button" disabled={Boolean(review.pending)} onClick={() => openDialog('resume')}>Resume subscription</button>}
              {plan.status === 'cancelled' && <button className="outlined" type="button" disabled={Boolean(review.pending)} onClick={() => openDialog('reactivate')}>Reactivate subscription</button>}
              {plan.status === 'active' && <button
                className="outlined"
                type="button"
                disabled={Boolean(review.pending) || !allowedAction(plan, 'pause')}
                onClick={() => openDialog('pause')}
              >
                Pause subscription
              </button>}
              {plan.status !== 'cancelled' && <button
                className="cancel-link"
                type="button"
                disabled={Boolean(review.pending) || !allowedAction(plan, 'cancel')}
                onClick={() => openDialog('cancel')}
              >
                Cancel subscription
              </button>}
              <p>Review the customer's request and affected shipments before confirming.</p>
            </div>
          </section>

          <section className="coverage-reminder">
            <Icon id="check" />
            <div>
              <strong>Colic Coverage</strong>
              <p>Check enrollment and any impact of a schedule change.</p>
              <button
                className="text-btn"
                type="button"
                disabled={Boolean(review.pending)}
                onClick={() => openDialog('coverage')}
              >
                Review coverage
              </button>
            </div>
          </section>
        </aside>
      </div>
      </SubscriptionBoundary>

      <UpNext customerId={customerId} />
      <ActionFooter
        disabled={true}
        noteWhenDisabled={finishReason(review)}
        noteWhenEnabled="Ready to continue"
        summaryBody="Review any open subscription changes before finishing."
      />

      <SubscriptionDialog open={dialogKey !== null} action={dialogKey} state={review} busy={busy}
        onClose={closeEditor} onInput={updateInput} onAgree={updateAgreement} onSave={value => void save(value)}
        onDiscard={discard} onCheck={() => void check()}
        onPaymentPreview={() => setReview(value => ({ ...value, message: 'Secure payment update is not connected in this preview. No payment page was opened and no link was sent.', error: false }))} />

    </WorkspaceChrome>
  );
}

/**
 * R9 boundary wrapper around the subscription grid. Keeps the main
 * component tidy and lets `useMemo` memoise the state resolution
 * next to the signal it depends on.
 */
function SubscriptionBoundary({
  signals,
  children,
}: {
  signals?: ScreenCompanionSignals;
  children: ReactNode;
}): ReactNode {
  const state = useMemo(
    () => resolveCompanionState(signals ?? {}),
    [signals],
  );
  return (
    <DataBoundary state={state} testId="subscriptions-boundary">
      {children}
    </DataBoundary>
  );
}

export default SubscriptionsReviewScreen;
